import { Component, Input, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ConfigService } from 'src/app/services/config/config.service';
import { Navigator } from "src/app/services/routing/navigator";
import { AppLinksService } from 'src/app/services/applinks/app-links.service';
import { GeoResponse } from 'src/app/interfaces/geo-interface';
import { GeoLocationControllerService } from 'src/app/services/geo-location/geo-location-controller.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { RoutingService } from 'src/app/services/routing/routing.service';
import { EventsService } from 'src/app/services/event/event.service';

@Component({
  selector: 'app-geo-location-view',
  templateUrl: './geo-location-view.component.html',
  styleUrls: ['./geo-location-view.component.scss'],
})
export class GeoLocationViewComponent implements OnInit {

  @Input() geoComplyError: any;

  public FEATURE_GEOCOMPLY_DOWNLOAD_URL: string;
  public FEATURE_DISPLAY_GEO_TROUBLESHOOTER_MSG: string;
  public FEATURE_DISPLAY_GEO_TROUBLESHOOTER_FALLBACK_MSG: string;
  public isAuthenticated: boolean;
  public geoLocationSubscription: Subscription;
  public showGeoLocationViewInformation: boolean;
  private display_geo_error: any;
  public downloadLink: string;
  public downloadMessage: string;
  public troubleshooter_message: string;
  public troubleshooter_fallback_message: string;
  public installationVerified: boolean = false;
  public errorMessage: string;
  private isMobileApp: boolean;
  private isMobileWeb: boolean;
  private isDesktopWeb: boolean;
  private isIosMobileApp: boolean;
  private isAndroidMobileApp: boolean;


  constructor(
    private configService: ConfigService,
    public navigator: Navigator,
    private routingService: RoutingService,
    private platform: Platform,
    private utilsService: UtilsService,
    private geoLocationControllerService: GeoLocationControllerService,
    private eventsService: EventsService,
    private appLinksService: AppLinksService,
  ) {
    const platformInfo = this.utilsService.getPlatformInfo();
    this.isMobileApp = platformInfo.isMobileApp;
    this.isMobileWeb = platformInfo.isMobileWeb;
    this.isDesktopWeb = platformInfo.isDesktopWeb;
    this.isIosMobileApp = platformInfo.isIosApp;
    this.isAndroidMobileApp = platformInfo.isAndroidApp;
  }

  ngOnInit() {
    if (this.geoComplyError) this.display_geo_error = this.geoComplyError;
    this.listenForGeoLocationUpdates();
  }

  ngOnDestroy(): void {

  }

  async showLoader(): Promise<void> {
    await this.geoLocationControllerService.showGeoLocationIndicatior();
  }

  async hideLoader(): Promise<void> {
    await this.geoLocationControllerService.hideGeoLocationIndicatior();
  }

  public listenForGeoLocationUpdates(): void {
    if (this.display_geo_error) {
      this.showGeoLocationViewInformation = true;
      this.displayGeoLocationError();
    } else {
      this.showGeoLocationViewInformation = false;
    }
  }

  public displayGeoLocationError(): void {
    // Extract error code for easier access
    const errorCode = this.display_geo_error.error_code;
    console.log(errorCode)
    switch (errorCode) {
      case 803:
        // Case for error code 803
        //Location is not enabled display custom error message
        this.errorMessage = this.configService.getConfig().FEATURE_GEOLOCATION_GEOCOMPLY_LOCATION_OFF_ERROR_MSG ?? 'You have denied location access. Please enable it in your browser settings.';
        console.log(this.errorMessage)
        return;

      case 612:
      case 613:
      case 615:
        // Case for error codes 612, 613, 615
        this.handleGeolocationSoftwareDownload(errorCode);
        return;
      case 611:
        switch (true) {
          case this.isIosMobileApp:
            return this.errorMessage = this.configService.getConfig().FEATURE_GEOLOCATION_GEOCOMPLY_LOCATION_OFF_ERROR_MSG_IOS_APP ?? 'It appears that your location services are currently disabled or unavailable, please enable Location services, turn ON "Precise location.';

          case this.isAndroidMobileApp:
            return this.errorMessage = this.configService.getConfig().FEATURE_GEOLOCATION_GEOCOMPLY_LOCATION_OFF_ERROR_MSG_ANDROID_APP ?? 'It appears that your location services are currently disabled or unavailable, please enable Location services, turn ON "Precise location.';

          case this.isMobileWeb:
            return this.errorMessage = this.configService.getConfig().FEATURE_GEOLOCATION_GEOCOMPLY_LOCATION_OFF_ERROR_MSG_MOBILE_WEB ?? 'It appears that your location services are currently disabled or unavailable, please enable Location services, turn ON "Precise location.';
          
          default:
            return this.errorMessage = this.display_geo_error.troubleshooter_message;
        }
      case 637:
        this.errorMessage = this.display_geo_error.troubleshooter_message;
        console.log(this.errorMessage)
        return;
      case 113:
        // Case for error codes 113
        this.downloadLink = this.getGeoGuardLink();
        return;
      default:
        // Default case for handling troubleshooter message when there is no need to handle/show error code related custom error message on UI
        this.handleTroubleshooterMessage();
        return;
    }
  }

  // Function to handle geolocation software download based on error codes 612, 613, 615
  private handleGeolocationSoftwareDownload(errorCode: string): void {
    this.configService.isLoaded().then(() => {
      const config = this.configService.getConfig();
      const FEATURE_GEOCOMPLY_DOWNLOAD_URL = config.FEATURE_GEOCOMPLY_DOWNLOAD_URL;
      const FEATURE_GEOCOMPLY_OPERATOR_ID = config.FEATURE_GEOCOMPLY_OPERATOR_ID;
      const FEATURE_GEOCOMPLY_CLIENT_VERSION = config.FEATURE_GEOCOMPLY_CLIENT_VERSION;

      let os: string;
      if (navigator.appVersion.indexOf("Win") !== -1) {
        os = 'win';
      }
      if (/Macintosh/.test(window.navigator.userAgent) && !this.platform.is("ipad")) {
        os = 'mac';
      }

      if (os) {
        this.downloadLink = `${FEATURE_GEOCOMPLY_DOWNLOAD_URL}?id=${FEATURE_GEOCOMPLY_OPERATOR_ID}&os=${os}&version=${FEATURE_GEOCOMPLY_CLIENT_VERSION}`;
        if (errorCode === '615') {
          this.downloadMessage = "A new version of the geolocation software is available. Download the update and follow the instructions to install.";
        } else {
          this.downloadMessage = "Download the geolocation software and follow the instructions to install.";
        }
      } else {
        this.errorMessage = "Geolocation software is not supported on your operating system.";
      }
    });
  }

  // Function to handle troubleshooter messages
  private handleTroubleshooterMessage(): void {
    const xmlTroubleShootMsg = this.parseXml(this.display_geo_error.troubleshooter_message);

    if (this.configService.getConfig().FEATURE_DISPLAY_GEO_TROUBLESHOOTER_MSG === '1') {
      if (xmlTroubleShootMsg) {
        const messageValue = xmlTroubleShootMsg.getElementsByTagName("message")[0].childNodes[0].nodeValue;
        this.troubleshooter_message = "";
        this.errorMessage = "";
        let messageValue2 = "";
        let messageValue3 = "";
        let messageValue4 = "";

        if (messageValue) {
          this.troubleshooter_message = messageValue;
          this.errorMessage = this.troubleshooter_message;

          // Add further messages if available
          if (xmlTroubleShootMsg.getElementsByTagName("message").length > 1) {
            messageValue2 = xmlTroubleShootMsg.getElementsByTagName("message")[1].childNodes[0].nodeValue;
            this.errorMessage += '<br/>' + messageValue2;
            if (xmlTroubleShootMsg.getElementsByTagName("message")[2]) messageValue3 = xmlTroubleShootMsg.getElementsByTagName("message")[2].childNodes[0].nodeValue;
            this.errorMessage += '<br/>' + messageValue3;
            if (xmlTroubleShootMsg.getElementsByTagName("message")[3]) messageValue4 = xmlTroubleShootMsg.getElementsByTagName("message")[3].childNodes[0].nodeValue;
            this.errorMessage += '<br/>' + messageValue4;
          }
          return;
        }
        console.log(this.troubleshooter_message);
      } else {
        // Fallback message if troubleshooter message is not returned
        this.troubleshooter_fallback_message = this.configService.getConfig().FEATURE_DISPLAY_GEO_TROUBLESHOOTER_FALLBACK_MSG
          ? this.configService.getConfig().FEATURE_DISPLAY_GEO_TROUBLESHOOTER_FALLBACK_MSG
          : "Sorry, something went wrong. We couldn't determine your current location.";
        this.errorMessage = this.troubleshooter_fallback_message;
        return;
      }
    }
  }

  private getGeoGuardLink() {
    this.downloadMessage = "Please open the GeoGuard Location Validator to geolocate";
    if (this.platform.is("android"))
      return (
        this.configService
          .getExternalLinks()
          .payload.filter((link) => link?.code == "oobee_applink")[0]?.url ??
        "https://play.google.com/store/apps/details?id=com.geocomply.oobeeapp&hl=en"
      );
    if (this.platform.is("ios"))
      return (
        this.configService
          .getExternalLinks()
          .payload.filter((link) => link?.code == "oobee_applink_ios")[0]
          ?.url ?? "https://itunes.apple.com/us/app/oobee/id921993633"
      );
  }

  public downloadGeoGuard() {
    const applink: string = this.getGeoGuardLink();
    return (window.location.href = applink);
  }


  private parseXml(xmlStr) {
    try {
      const parser = new window.DOMParser();
      const xmlDoc = parser.parseFromString(xmlStr, "text/xml");

      // Check for errors in the XML parsing (e.g., malformed XML)
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        throw new Error("XML Parsing error: " + parseError[0].textContent);
      }

      return xmlDoc;
    } catch (e) {
      console.warn("Error parsing XML: ", e);
    }
  }

  closeModal = () => {
    this.navigator.dismiss();
    this.launchGameAfterInstallation();
  }

  public onDownload() {
    return this.appLinksService.openExternalPage(this.downloadLink);
  }

  onVerifyInstallation() {
    //should we display loading indication on verify action ?
    this.geoLocationControllerService.cleanupGeoLocation();
    this.geoLocationControllerService.executeGeoLocationProcess()
      .then((geoResp: GeoResponse) => {
        console.log(geoResp)
        if (geoResp.is_allowed) {
          this.installationVerified = true;
        } else {
          console.log('Geo Location Failed on verify Geo Action')
        }
      });
  }

  onContinue() {
    this.routingService.defaultRoute();
    this.launchGameAfterInstallation();
  }

  launchGameAfterInstallation(){
    if(this.configService.getConfig().FEATURE_GEOLOCATION_GEOCOMPLY_LAUNCH_GAME_AFTER_INSTALLATION === '1'){
      if(this.installationVerified)this.eventsService.publish('event:game:restart');
    }
  }

}
